/**
 * Cart Toast Notification Handler
 * Shows a top-right notification when a product is added to cart
 * Prevents cart drawer from opening on add to cart
 * Does not show notification on quantity updates
 */

class CartToastNotification {
  constructor() {
    this.notification = document.getElementById('cart-toast-notification');
    if (!this.notification) return;

    this.lastCartCount = this.getCartCount();
    this.lastCartItemCount = this.getCartItemCount();
    this.isAddingToCart = false;
    this.isUpdatingQuantity = false;
    
    this.init();
    this.preventDrawerOnAddToCart();
    this.fixCartDrawerOpening();
  }

  getCartCount() {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon) {
      const countElement = cartIcon.querySelector('.cart-count-bubble span');
      if (countElement) {
        const count = parseInt(countElement.textContent.trim());
        return isNaN(count) ? 0 : count;
      }
    }
    return 0;
  }

  getCartItemCount() {
    // Get the actual number of unique items in cart (not total quantity)
    // This helps distinguish between adding new item vs updating quantity
    // Try to get from cart drawer first
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const cartItems = cartDrawer.querySelectorAll('cart-item, [id^="CartDrawer-Item-"]');
      if (cartItems.length > 0) {
        return cartItems.length;
      }
    }
    
    // Fallback: try to get from cart icon bubble or any cart count indicator
    // If we can't determine, return the total count as fallback
    return this.getCartCount();
  }

  init() {
    // Intercept fetch requests to detect add to cart vs quantity update
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = function(...args) {
      const url = args[0];
      const options = args[1] || {};
      
      // Check if this is a cart add request (new item)
      if (typeof url === 'string' && url.includes('/cart/add') && options.method === 'POST') {
        // Store previous counts before the request
        const prevItemCount = self.getCartItemCount();
        const prevCount = self.getCartCount();
        
        self.isAddingToCart = true;
        self.isUpdatingQuantity = false;
        
        return originalFetch.apply(this, args).then((response) => {
          if (response.ok) {
            // Wait for cart to update, then check if it's a new item
            // Use multiple checks with increasing delays to catch the update
            const checkCartUpdate = (attempt = 0) => {
              const maxAttempts = 5;
              const delay = 100 + (attempt * 100); // 100ms, 200ms, 300ms, etc.
              
              setTimeout(() => {
                const currentItemCount = self.getCartItemCount();
                const currentCount = self.getCartCount();
                
                // If cart was empty (prevCount = 0) and now has items, it's definitely a new item
                if (prevCount === 0 && currentCount > 0) {
                  // First item added - always show notification
                  self.show();
                  self.lastCartItemCount = currentItemCount;
                  self.lastCartCount = currentCount;
                  setTimeout(() => {
                    self.isAddingToCart = false;
                  }, 500);
                  return;
                }
                
                // Check if item count increased (new item added)
                if (currentItemCount > prevItemCount) {
                  // New item added - show notification
                  self.show();
                  self.lastCartItemCount = currentItemCount;
                  self.lastCartCount = currentCount;
                  setTimeout(() => {
                    self.isAddingToCart = false;
                  }, 500);
                  return;
                }
                
                // If count increased but item count detection failed, use count as fallback
                // This handles the case where cart drawer items aren't rendered yet
                if (currentCount > prevCount && attempt < maxAttempts) {
                  // Try again after a short delay
                  checkCartUpdate(attempt + 1);
                  return;
                }
                
                // If we've tried multiple times and count increased, assume it's a new item
                if (currentCount > prevCount && attempt >= maxAttempts - 1) {
                  self.show();
                  self.lastCartItemCount = currentItemCount;
                  self.lastCartCount = currentCount;
                  setTimeout(() => {
                    self.isAddingToCart = false;
                  }, 500);
                  return;
                }
                
                // Update counts even if we didn't show notification
                if (currentCount !== prevCount) {
                  self.lastCartCount = currentCount;
                  self.lastCartItemCount = currentItemCount;
                }
                
                // Reset flag if we've exhausted attempts
                if (attempt >= maxAttempts - 1) {
                  setTimeout(() => {
                    self.isAddingToCart = false;
                  }, 500);
                }
              }, delay);
            };
            
            checkCartUpdate();
          } else {
            self.isAddingToCart = false;
          }
          return response;
        }).catch(() => {
          self.isAddingToCart = false;
        });
      }
      
      // Check if this is a quantity update request
      if (typeof url === 'string' && (url.includes('/cart/change') || url.includes('/cart/update')) && options.method === 'POST') {
        self.isUpdatingQuantity = true;
        self.isAddingToCart = false;
        
        return originalFetch.apply(this, args).then((response) => {
          if (response.ok) {
            setTimeout(() => {
              self.lastCartCount = self.getCartCount();
              self.lastCartItemCount = self.getCartItemCount();
              self.isUpdatingQuantity = false;
            }, 100);
          }
          return response;
        });
      }
      
      return originalFetch.apply(this, args);
    };

    // Listen for cart icon bubble updates (but don't show notification for quantity updates)
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon) {
      const iconObserver = new MutationObserver((mutations) => {
        // Only process if we're adding to cart, not updating quantity
        if (this.isAddingToCart && !this.isUpdatingQuantity) {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' || mutation.type === 'characterData') {
              const currentCount = this.getCartCount();
              const currentItemCount = this.getCartItemCount();
              
              // If cart was empty and now has items, show notification
              if (this.lastCartCount === 0 && currentCount > 0) {
                setTimeout(() => {
                  this.show();
                }, 100);
                this.lastCartCount = currentCount;
                this.lastCartItemCount = currentItemCount;
                return;
              }
              
              // Only show if item count increased (new item added)
              if (currentItemCount > this.lastCartItemCount) {
                setTimeout(() => {
                  this.show();
                }, 100);
                this.lastCartItemCount = currentItemCount;
                this.lastCartCount = currentCount;
              } else if (currentCount > this.lastCartCount && this.lastCartCount === 0) {
                // Fallback: if count increased from 0, it's a new item
                setTimeout(() => {
                  this.show();
                }, 100);
                this.lastCartCount = currentCount;
                this.lastCartItemCount = currentItemCount;
              }
            }
          });
        }
      });

      iconObserver.observe(cartIcon, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }

  preventDrawerOnAddToCart() {
    // Wait for cart drawer to be available
    const waitForCartDrawer = () => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        setTimeout(waitForCartDrawer, 100);
        return;
      }

      // Store original methods
      const originalRenderContents = cartDrawer.renderContents;
      const originalOpen = cartDrawer.open;
      const self = this;
      let cartNeedsRefresh = false;

      // Override renderContents to prevent opening drawer on add to cart
      if (originalRenderContents) {
        cartDrawer.renderContents = function(parsedState, dontOpen = false) {
          // If we're adding to cart (not updating quantity), prevent drawer from opening
          if (self.isAddingToCart && !self.isUpdatingQuantity) {
            // Mark that cart needs refresh when manually opened
            cartNeedsRefresh = true;
            
            // Update cart sections properly by calling original renderContents with dontOpen=true
            // This ensures all sections are updated correctly
            if (parsedState) {
              try {
                // Temporarily set a flag to prevent the open() call inside renderContents
                const wasAddingToCart = self.isAddingToCart;
                // Call original with dontOpen=true to update sections without opening
                originalRenderContents.call(cartDrawer, parsedState, true);
                // Restore flag
                self.isAddingToCart = wasAddingToCart;
              } catch (e) {
                // Fallback: manual section update if original fails
                if (parsedState.sections) {
                  Object.keys(parsedState.sections).forEach((sectionId) => {
                    const sectionElement = document.getElementById(sectionId);
                    if (sectionElement && parsedState.sections[sectionId]) {
                      try {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(parsedState.sections[sectionId], 'text/html');
                        const newContent = doc.body.innerHTML || doc.documentElement.innerHTML || parsedState.sections[sectionId];
                        sectionElement.innerHTML = newContent;
                      } catch (err) {
                        sectionElement.innerHTML = parsedState.sections[sectionId];
                      }
                    }
                  });
                }
              }
            }
            // Don't proceed with opening the drawer
            return;
          }
          
          // For quantity updates or manual cart icon clicks, use original behavior
          return originalRenderContents.call(this, parsedState, dontOpen);
        };
      }

      // Also prevent opening via the open method when triggered by add to cart
      if (originalOpen) {
        cartDrawer.open = function(trigger) {
          // If we're adding to cart, don't open the drawer
          if (self.isAddingToCart && !self.isUpdatingQuantity) {
            return;
          }
          
          // If cart was recently updated and drawer is being manually opened, refresh it first
          if (cartNeedsRefresh && !self.isAddingToCart) {
            cartNeedsRefresh = false;
            
            // Fetch latest cart contents before opening
            if (window.routes && window.routes.cart_url) {
              fetch(window.routes.cart_url + '?sections=cart-drawer,cart-icon-bubble')
                .then(response => response.json())
                .then(data => {
                  if (data && data.sections && originalRenderContents) {
                    // Update drawer contents with fresh data
                    originalRenderContents.call(cartDrawer, data, false);
                    // Then open the drawer
                    setTimeout(() => {
                      originalOpen.call(cartDrawer, trigger);
                    }, 50);
                  } else {
                    // If no data, just open normally
                    originalOpen.call(cartDrawer, trigger);
                  }
                })
                .catch(() => {
                  // If fetch fails, just open normally
                  originalOpen.call(cartDrawer, trigger);
                });
              return; // Don't call originalOpen yet, wait for fetch
            }
          }
          
          // Use original open method
          return originalOpen.call(this, trigger);
        };
      }
    };

    waitForCartDrawer();
  }

  fixCartDrawerOpening() {
    // Fix cart drawer opening issue by ensuring click handlers are properly attached
    const cartIcons = document.querySelectorAll('.header__icon--cart');
    const cartDrawer = document.querySelector('cart-drawer');
    
    if (cartIcons.length > 0 && cartDrawer) {
      cartIcons.forEach((cartIcon) => {
        // Remove existing click handlers to avoid duplicates
        const newCartIcon = cartIcon.cloneNode(true);
        cartIcon.parentNode.replaceChild(newCartIcon, cartIcon);
        
        // Add click handler - this should always work for manual clicks
        newCartIcon.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Reset flags to allow drawer to open on manual click
          this.isAddingToCart = false;
          this.isUpdatingQuantity = false;
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open(newCartIcon);
          }
        });
      });
    }
  }

  show() {
    if (!this.notification) return;

    // Add show class
    this.notification.classList.add('show');

    // Auto-hide after 3 seconds
    setTimeout(() => {
      this.hide();
    }, 3000);
  }

  hide() {
    if (!this.notification) return;
    this.notification.classList.remove('show');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartToastNotification();
  });
} else {
  new CartToastNotification();
}

